import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useSharedValidations } from '../../../../hooks/useSharedValidations';
import { zodResolver } from '@hookform/resolvers/zod';
import { message, Col, Row } from 'antd';
import Button from '../../../atoms/Button';
import TitleText from '../../../atoms/TitleText';
import {
  useAddProfileBranchMutation,
  useEditProfileBranchMutation,
  useAddProfileWarehouseMutation,
  useEditProfileWarehouseMutation,
} from '../../../../store/services/sellerProfileCreationService'; import { getFirstValidationError } from '../../../../lib/apiErrors'; import FormField from '../../../FormField/FormField';
import Map from '../../../atoms/Map';

export default function LocationForm({
  profileId,
  initData,
  onSuccess,
  type,
}: {
  profileId: string;
  initData?: any;
  onSuccess: () => void;
  type: 'BRANCH' | 'WAREHOUSE';
}) {
  const { t } = useTranslation();

  const [addBranch, { isLoading: isAddBranchLoading }] = useAddProfileBranchMutation();
  const [editBranch, { isLoading: isEditBranchLoading }] = useEditProfileBranchMutation();
  const [addWarehouse, { isLoading: isAddWarehouseLoading }] = useAddProfileWarehouseMutation();
  const [editWarehouse, { isLoading: isEditWarehouseLoading }] = useEditProfileWarehouseMutation();

  const { zodRequiredStringValidate } = useSharedValidations();
  const required_error = t('components.validations.requiredField');

  const schema = z.object({
    name: zodRequiredStringValidate
      .min(3, {
        message: t('components.validations.minLength', { value: 3 }),
      })
      .max(50, {
        message: t('components.validations.maxLength', { value: 50 }),
      }),
    lat: z.number({ required_error }).min(-90).max(90),
    lng: z.number({ required_error }).min(-180).max(180),
  });

  type fieldsTypes = z.infer<typeof schema>;

  const methods = useForm<fieldsTypes>({
    mode: 'all',
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      lat: null as any,
      lng: null as any,
    },
  });

  const { reset } = methods;

  const [mapLocation, setMapLocation] = useState<{
    lat: number | undefined;
    lng: number | undefined;
    currentAddress?: string;
    countryCode?: string;
  }>({
    lat: undefined,
    lng: undefined,
    currentAddress: undefined,
    countryCode: undefined,
  });

  useEffect(() => {
    if (initData) {
      reset({
        name: initData.name || '',
        lat: initData.lat || null,
        lng: initData.lang || initData.lng || null,
      });
      if (initData.lat && (initData.lang || initData.lng)) {
        setMapLocation({
          lat: initData.lat,
          lng: initData.lang || initData.lng,
          currentAddress: initData.address,
          countryCode: initData.countryCode,
        });
      }
    }
  }, [initData, reset]);

  const updateLocation = useCallback((loc: any) => {
    setMapLocation(loc);
    methods.setValue('lat', loc.lat, { shouldValidate: true });
    methods.setValue('lng', loc.lng, { shouldValidate: true });
  }, [methods]);

  const onSubmit = async (data: fieldsTypes) => {
    try {
      const payload = {
        ...(initData?.id ? { id: initData.id } : {}),
        name: data.name,
        address: mapLocation.currentAddress || initData?.address || '',
        lat: Number(data.lat),
        lang: Number(data.lng),
        countryCode: mapLocation.countryCode || initData?.countryCode || 'SA',
      };

      if (type === 'BRANCH') {
        if (initData?.id) await editBranch({ profileId, data: { ...payload, id: initData.id } }).unwrap();
        else await addBranch({ profileId, data: payload }).unwrap();
      } else {
        if (initData?.id) await editWarehouse({ profileId, data: { ...payload, id: initData.id } }).unwrap();
        else await addWarehouse({ profileId, data: payload }).unwrap();
      }
      onSuccess();
    } catch (err: any) {
      const serverMessage = getFirstValidationError(err);
      if (serverMessage) {
        const normalized = serverMessage.toLowerCase();
        if (normalized.includes('branch name') || normalized.includes('warehouse name')) {
          methods.setError('name', { type: 'server', message: serverMessage });
          return;
        }

        message.error(serverMessage);
        return;
      }

      message.error(t('components.somethingWentWrong'));
    }
  };

  const isLoading = isAddBranchLoading || isEditBranchLoading || isAddWarehouseLoading || isEditWarehouseLoading;
  const nameLabel = type === 'BRANCH' ? t('components.branch_name') : t('components.warehouse_name');
  const namePlaceholder = type === 'BRANCH' ? t('components.branch_name_placeholder') : t('components.warehouse_name_placeholder');

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)} data-testid={`profile-creation-add-${type.toLowerCase()}-form`}>
        <Row gutter={[24, 24]}>
          <Col span={24}>
            <FormField
              type="text"
              id="name"
              name="name"
              label={nameLabel}
              placeholder={namePlaceholder}
            />
          </Col>
          <Col span={24}>
            <TitleText size="sm" text={type === 'BRANCH' ? t('components.branch_location') : t('components.warehouse_location')} className="mb-2 mt-1" />
            <div className="h-[300px] w-full rounded-lg overflow-hidden border border-light-input-border dark:border-dark-input-border" data-testid={`profile-creation-add-${type.toLowerCase()}-map-container`}>
              <Map
                withSearch
                updateLocation={updateLocation}
                location={mapLocation}
                height="300px"
                width="100%"
              />
            </div>
            <div className="hidden">
              <FormField type="number" id="lat" name="lat" />
              <FormField type="number" id="lng" name="lng" />
            </div>
          </Col>
        </Row>
        <div className="flex flex-row-reverse mt-10" data-testid="location-form-actions">
          <Button type="submit" disabled={isLoading}>
            {t('components.done')}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
